import { ApolloProvider } from '@apollo/client';
import { Button } from '@mui/material';
import { SearchkitClient, SearchkitProvider } from '@searchkit/client';
import React, { useCallback, useContext, useState } from 'react';
import { UserContext } from '../common/auth/UserContext';
import Loading from '../common/components/Loading';
import { ENTITY_CONSTANTS } from '../common/constants/entityConstants';
import { FEATURE_CONSTANTS } from '../common/constants/featureConstants';
import { PREFERENCE_ENTITY_CONTEXTS_CONSTANT } from '../common/constants/preferenceConstants';
import I18n from '../common/i18n/I18n';
import { Icons } from '../common/icons/Icons';
import { H, V } from '../common/Layout';
import DataList from '../common/list/DataList';
import ListView from '../common/list/ListView';
import { searchClient, slimResultsQuery } from '../common/list/slimQuery';
import QuickAddOrganizationFormModal from '../organization/QuickAddOrganizationFormModal';
import { MarketplaceProfileDetails } from './MarketplaceProfileDetails';

export const MainTile = ({ item, index, shortlistLineItems, onAddToShortlist = undefined, handleItemClick }) => {
    const gap = 3;

    const commonProps = {
        item,
        gap,
        index,
        onAddToShortlist,
        shortlistLineItems,
        handleItemClick
    };

    return <OrganizationLayout {...commonProps} />;
};

const OrganizationLayout = ({ item: organization, onAddToShortlist, shortlistLineItems, handleItemClick }) => {
    return (
        <MarketplaceProfileDetails
            item={organization}
            shortlistLineItems={shortlistLineItems}
            onAddToShortlist={onAddToShortlist}
            isOrganizationDetails
            onSeeMore={() => handleItemClick(organization)}
        />
    );
};

const List = ({
    id,
    loading,
    shortlistLineItems,
    data,
    isInSidePanel = false,
    onAddToShortlist,
    setOrganizationModalState,
    setIsEditorOpen,
    handleItemClick,
    ...props
}) => {
    const ItemRenderer = useCallback(
        ({ item, index, ...props }) => (
            <MainTile
                index={index}
                item={item}
                shortlistLineItems={shortlistLineItems}
                handleItemClick={handleItemClick}
                onAddToShortlist={onAddToShortlist}
                {...props}
            />
        ),
        [handleItemClick, onAddToShortlist, shortlistLineItems]
    );

    return data ? (
        <DataList
            id={id}
            items={data.results.hits.items}
            border
            divider
            loading={loading}
            listItemSx={{
                '&:hover': {
                    backgroundColor: 'transparent',
                    cursor: 'default'
                }
            }}
            pagination={data.results.hits.page}
            ItemRenderer={ItemRenderer}
            onClick={() => {}}
            disableRipple={isInSidePanel}
            {...props}
            emptyListElement={
                <V sx={{ alignItems: 'center' }}>
                    <Button
                        onClick={() => {
                            setOrganizationModalState(true);
                        }}
                    >
                        <I18n token="form.organization.create" />
                    </Button>
                </V>
            }
        />
    ) : (
        <Loading />
    );
};

export default function OrganizationMarketplaceList({
    id = 'mp-list',
    showBreadCrumbs = true,
    prefilters = [],
    shortlistLineItems,
    immutableFilters = [],
    hasFacets = true,
    isInSidePanel = false,
    onAddToShortlist = undefined,
    saveFiltersDisabled = false,
    resourceData = undefined,
    handleItemClick,
    entity = ENTITY_CONSTANTS.organization
}) {
    const searchkit = new SearchkitClient();

    const { application } = useContext(UserContext);
    const [organizationModalState, setOrganizationModalState] = useState(false);
    const [search, setSearch] = useState('');

    const EntityListRenderer = useCallback(
        (props) => (
            <List
                isInSidePanel={isInSidePanel}
                onAddToShortlist={onAddToShortlist}
                shortlistLineItems={shortlistLineItems}
                setOrganizationModalState={setOrganizationModalState}
                handleItemClick={handleItemClick}
                {...props}
            />
        ),
        [isInSidePanel, onAddToShortlist, shortlistLineItems, handleItemClick]
    );

    return (
        <ApolloProvider client={searchClient}>
            <SearchkitProvider client={searchkit}>
                <>
                    <ListView
                        id={id}
                        entity={entity}
                        feature={FEATURE_CONSTANTS.organizationUnits}
                        preferenceEntityKey={PREFERENCE_ENTITY_CONTEXTS_CONSTANT.marketplace}
                        uri={`${application.home}/marketplace`}
                        query={slimResultsQuery('Organization')}
                        EntityListRenderer={EntityListRenderer}
                        quickFilters={[
                            {
                                identifier: 'isSharedInDirectory',
                                label: (
                                    <H>
                                        <Icons.ShowrunnrFilter />
                                    </H>
                                ),
                                tooltip: <I18n token="marketplace.tooltip" />
                            }
                        ]}
                        enableSearch
                        searchQuery={search}
                        promoteFacets={hasFacets && ['type']}
                        preFilters={prefilters}
                        showBreadCrumbs={showBreadCrumbs}
                        defaultSortModel={{ field: 'updatedOn', sort: 'desc' }}
                        containerSx={isInSidePanel && { flexGrow: 1, margin: 0 }}
                        saveFiltersDisabled={saveFiltersDisabled}
                        immutableFiltersWithSameIdentifier={immutableFilters}
                    />
                    {organizationModalState ? (
                        <QuickAddOrganizationFormModal
                            id={id}
                            setOrganizationModalState={setOrganizationModalState}
                            organizationModalState={organizationModalState}
                            resourceData={resourceData}
                            setSearch={setSearch}
                            onAddToShortlist={onAddToShortlist}
                        />
                    ) : (
                        <></>
                    )}
                </>
            </SearchkitProvider>
        </ApolloProvider>
    );
}
